<template>
  <v-container>
    <v-row>
      <v-col>
        <DragAndDropFile
            ref="dragAndDrop"
            custom-height="80vh"
            not-required
            :view-only="viewOnly"
            @fileChange="setFile($event)"
        />
      </v-col>
      <v-col>
        <v-form ref="form">
          <v-row justify="space-between">
            <v-col cols="12" :md="signMode ? 4 : 6">
              <v-container>
                <v-row>
                  <v-col>
                    <CustomerTypeAhead
                        ref="customerTypeahead"
                        :value="offerDocumentation.supplierId"
                        @input="($event) => offerDocumentation.supplierId = $event"
                        @inputName="($event) => offerDocumentation.supplier = $event"
                        :label="$t('book/issue:common.supplier')"
                        required
                        :suppliersOnly="true"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="offerDocumentation.price"
                      :readonly="viewOnly"
                      clearable
                      outlined
                      :label="$t('common:price')"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                        v-model="offerDocumentation.externalId"
                        :readonly="viewOnly"
                        clearable
                        outlined
                        :label="$t('book/issue:common.offer-id')"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea
                      :label="$t('common:noteInfo')"
                        :readonly="viewOnly"
                        clearable
                        outlined
                        v-model="offerDocumentation.description" />
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" :md="signMode ? 4 : 6">
              <v-container>
                <v-row>
                  <v-col>
                    <DateField
                      ref="validFrom"
                      :readonly="viewOnly"
                      @update="(v) => offerDocumentation.startDate = v"
                      :key="validFromKey"
                      :label="$t('common:valid-from')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <DateField
                      ref="validUntil"
                      :readonly="viewOnly"
                      @update="(v) => offerDocumentation.endDate = v"
                      :key="validUntilKey"
                      :label="$t('common:valid-until')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <DateField
                      ref="appointmentDate"
                      :readonly="viewOnly"
                      @update="(v) => offerDocumentation.appointmentDate = v"
                      :key="appointmentDateKey"
                      :label="$t('book/issue:common.appointment-date')"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="12" md="4" v-if="signMode">
              <v-checkbox
                :readonly="offerDocumentation.signed"
                :label="$t('book/issue:common.factually-correct')"
                v-model="offerDocumentation.factuallyCorrect" />
              <v-checkbox
                :readonly="offerDocumentation.signed"
                :label="$t('book/issue:common.technically-correct')"
                v-model="offerDocumentation.technicallyCorrect" />
              <v-checkbox
                :readonly="offerDocumentation.signed"
                :label="$t('common:signed')"
                v-model="offerDocumentation.toBeSigned" />
              <v-checkbox
                :readonly="offerDocumentation.signed"
                :label="$t('book/issue:common.recommended')"
                v-model="offerDocumentation.advised" />
              <v-checkbox
                :readonly="offerDocumentation.signed"
                :label="$t('book/issue:common.ab-present')"
                v-model="offerDocumentation.containAB" />
            </v-col>
          </v-row>
          <v-row v-if="!hideButton">
            <v-col>
              <BaseButton
                color="primary"
                :btnText="buttonLabel"
                @clickedButton="save()"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>

import OfferDocumentation from '@/modules/book/data/offer-documentation';
import DateField from '@/modules/core/components/DateField.vue';
import OfferDocumentationService from '@/modules/book/services/task-documentation/offer-documentation-service';
import Utils from '@/modules/core/services/utils';
import DragAndDropFile from '@/modules/core/components/DragAndDropFile';
import DocumentationService from '@/modules/core/services/documentation-service';
import {DocumentType} from '@/modules/book/data/document-type';
import BaseButton from "@/modules/core/components/Base/BaseButton.vue";
import CustomerTypeAhead from "@/modules/core/components/CustomerTypeAhead.vue";

export default {
  name: 'OfferDocumentationForm',
  components: {CustomerTypeAhead, BaseButton, DragAndDropFile, DateField},
  props: {
    hideButton: Boolean,
    viewOnly: Boolean,
    signMode: Boolean
  },
  data() {
    return {
      inputData: {},
      appointmentDateKey: '2',
      validUntilKey: '3',
      validFromKey: '5',
      offerDocumentation: new OfferDocumentation(),
      isNewItem: false
    }
  },
  computed: {
    buttonLabel() {
      if (this.offerDocumentation && this.offerDocumentation.id)
          return this.$t('save-changes');

      let buttonLabel = 'buttons:add-specific';
      return this.$t(buttonLabel, { specific: this.$t('book/issue:offerDocumentation') });
    }
  },
  methods: {
    setInputData(inputData) {
      this.inputData = inputData;
      this.offerDocumentation.task = inputData.taskId;
      this.offerDocumentation.customer = inputData.customerId;
      this.$refs.dragAndDrop.uploadAsUrl(inputData.file);
      this.file = inputData.file;
    },
    setCreateData(taskInfo) {
      this.inputData = taskInfo;
      this.offerDocumentation.task = taskInfo.taskId;
      this.offerDocumentation.customer = taskInfo.customerId;
      this.isNewItem = true;
    },
    setEditData(offer) {
      if (!offer) return;
      this.offerDocumentation = offer;

      if (this.offerDocumentation.startDate) {
        this.offerDocumentation.startDate =
            this.$refs.validFrom.parseDTO(this.offerDocumentation.startDate);
      }

      if (this.offerDocumentation.endDate) {
        this.offerDocumentation.endDate =
            this.$refs.validUntil.parseDTO(this.offerDocumentation.endDate);
      }

      if (this.offerDocumentation.appointmentDate) {
        this.offerDocumentation.appointmentDate =
            this.$refs.appointmentDate.parseDTO(this.offerDocumentation.appointmentDate);
      }

      if (this.offerDocumentation.supplierId !== null)
        this.$refs.customerTypeahead.initialise(this.offerDocumentation.supplierId);

      this.loadStaticFileUrl();
    },
    save(isSignMode) {
      this.$refs.dragAndDrop.isMandatoryFilledOut();
      if (!this.$refs.form.validate()
          || !this.$refs.dragAndDrop.isMandatoryFilledOut()) {
        document.getElementsByClassName("v-dialog--active")[0].scrollTop = 0;
        return;
      }

      if (isSignMode) {
        this.offerDocumentation.signed = true;
      }

      let label = this.offerDocumentation.id ? 'snackbar:update-success' : 'snackbar:add-success';
      let failLabel = this.offerDocumentation.id ? 'snackbar:update-failure' : 'snackbar:add-failure';
      OfferDocumentationService.saveOrUpdate(
          this.offerDocumentation,
          this.inputData.file,
          () => {
            Utils.showSuccessSnackbar(this.$t(label, {name: this.$t('book/issue:offerDocumentation')}))
            this.$emit('closeDialog');
          },
          () => {
            Utils.showFailureSnackbar(this.$t(failLabel, {name: this.$t('book/issue:offerDocumentation')}))
            this.$emit('closeDialog');
          }
      )
    },
    loadStaticFileUrl() {
      DocumentationService.getDocByTypeAndId(
          this.offerDocumentation.id,
          this.offerDocumentation.fileType,
          DocumentType.OFFER_TASK,
          this.offerDocumentation.task,
          (fileUrl) => {
            this.offerDocumentation.src = fileUrl;
            this.$refs.dragAndDrop.setPreviewSrc(this.offerDocumentation);
          },
          () => {
            this.$refs.dragAndDrop.clear();
          }
      )
    },
    setFile(item) {
      this.file = item;

      if (item) {
        let docuToParse = {
          fileType: item.type,
          src: URL.createObjectURL(item)
        }
        this.$refs.dragAndDrop.setPreviewSrc(docuToParse)
      } else {
        this.$refs.dragAndDrop.setPreviewSrc(null)
      }

      this.$refs.dragAndDrop.file = item;
      this.$refs.dragAndDrop.isUploading = false;
      this.inputData.file = item;
    },
    clearForm() {
      this.documentation = new OfferDocumentation();
      this.$refs.form.reset();
      this.validFromKey++;
      this.validUntilKey--;
      this.appointmentDateKey += 10;
      this.$refs.dragAndDrop.clear();
      this.isNewItem = false;
    }
  }
}
</script>